// `shortlist.pinned-mode.service.ng1.ts` — whether the panel is PINNED, and the header's pin.
//
// ⚠️ THREE LAYERS, AND THE HEADER ONLY EVER SETS THE MIDDLE ONE:
//
//   forcePinnedMode   the ROUTE's own `data.forcePinnedMode`. Overrides everything, and is
//                     why the pin disappears on the screens that declare it: nineteen routes
//                     do, and on those the mode is not the user's to choose.
//   manualPinnedMode  what the user last set, read back from the shell's stored view
//                     properties and written by the pin.
//   defaultPinnedMode `true` — the value when nothing above it applies.
//
// `isPinnedMode = forcePinnedMode ?? (manualPinnedMode ?? true)`, which is `calculatePinnedMode`
// written as an expression.
//
// ⚠️ AND IT IS NOT COMPUTED UNTIL SOMETHING ASKS FOR IT, which this port got wrong by being
// tidier than upstream. `isPinnedMode` is a FIELD there, undefined until `calculatePinnedMode()`
// runs — and the only caller that fires outside the iframe is `$stateChangeSuccess`, which this
// app's ui-router does not emit. So on a page that is NOT inside the extension, upstream's pin
// stays undefined and the template's `ng-class` renders `un-pinned`. Measured on the same
// tenant, same user, same route: careero's own app answers `shortlist-header-cta pin-cta
// un-pinned` in white, and this answered `pinned` in #f0a202 — 118 pixels of amber thumbtack
// on every signed-in reference, which is most of what `/client-contact/review` had left.
//
// Collapsing the three layers into one expression evaluated eagerly is what produced it, so
// the fix is to keep the expression and gate it: nothing is pinned until the frame has
// actually answered.
import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { IFrameEventType, iframeClient, isInIFrame } from '@woo-ext/protocol'
import { inheritedData } from './routeData.ts'

/** `defaultPinnedMode = true` (service:24). */
const DEFAULT_PINNED_MODE = true

export interface PinnedMode {
  /** `ctrl.shortlistPinnedModeService.isPinnedMode` — what the icon's class reflects. */
  pinned: boolean
  /**
   * `!forcePinnedMode && showPinIcon` — upstream's `ng-if` on the pin.
   *
   * ⚠️ THE FORCE LAYER HIDES THE CONTROL, it does not merely override it. On a screen that
   * forces the mode there is nothing to toggle, so offering the icon would be the inert
   * control this port keeps removing.
   */
  showPin: boolean
  /** `togglePinnedMode()` — sets the manual layer and tells the shell. */
  toggle: () => void
}

export function usePinnedMode(showPinIcon: boolean): PinnedMode {
  const { pathname } = useLocation()
  const [manual, setManual] = useState<boolean | undefined>(undefined)
  /**
   * Whether `calculatePinnedMode()` has had a reason to run — the frame answered, or the user
   * pressed the pin. Upstream has no such flag because it has no expression: it has a field
   * that simply is not set yet.
   */
  const [computed, setComputed] = useState(false)

  const forced = inheritedData(pathname, 'forcePinnedMode')
  const forcePinnedMode = typeof forced === 'boolean' ? forced : undefined

  useEffect(() => {
    // ⚠️ THE SHELL IS THE SOURCE, NOT THIS PAGE. `manualPinnedMode` is persisted per host by
    // the content script, so the first answer has to come back over the frame bridge —
    // upstream's `init()` does exactly this and warns, rather than throws, when there is no
    // parent to ask (a standalone tab).
    if (!isInIFrame()) return undefined
    let live = true
    void iframeClient()
      .request<{ viewProperties?: { pinnedMode?: boolean } }>(IFrameEventType.GetShortListViewProperties)
      .then(
        (response) => {
          if (!live) return
          const stored = response.viewProperties?.pinnedMode
          if (typeof stored === 'boolean') setManual(stored)
          // ⚠️ SET EVEN WHEN THE SHELL HAS NO STORED VALUE. `init()` assigns
          // `manualPinnedMode = viewProperties.pinnedMode` — which may be undefined — and then
          // calls `calculatePinnedMode()` regardless, so an answer of "nothing stored" is
          // still an answer and the default applies from that moment.
          setComputed(true)
        },
        () => {
          // "Error retrieving view properties, this might be because window is in a tab."
        },
      )
    return () => {
      live = false
    }
  }, [])

  // `isPinnedMode` — undefined until computed, which the header reads as un-pinned.
  const pinned = forcePinnedMode ?? (computed ? (manual ?? DEFAULT_PINNED_MODE) : false)

  const toggle = useCallback(() => {
    // `setManualPinnedMode(!isPinnedMode, true)` — upstream negates the DISPLAYED value, not
    // the manual layer, and the two differ whenever the manual layer is unset: on a page the
    // frame never answered the pin reads un-pinned, and negating `manual ?? true` would store
    // `false` and leave the icon exactly as it was — a control that visibly does nothing.
    const next = !pinned
    setManual(next)
    // `setManualPinnedMode` ends in `calculatePinnedMode()`, so a press is also what makes the
    // value exist on a page the frame never answered.
    setComputed(true)
    if (isInIFrame()) iframeClient().send(IFrameEventType.SetExtensionPinnedMode, { mode: next })
  }, [pinned])

  return {
    pinned,
    // ⚠️ `!forcePinnedMode`, WHICH IS TRUTHINESS AND NOT "IS IT SET". Upstream's `ng-if` hides
    // the pin only when the route forces it ON; a route forcing it OFF would still offer the
    // control. Every declared value in `routes.json` is `true` today, so the two readings agree
    // on the current table — this is the one that keeps agreeing if a `false` ever appears.
    showPin: showPinIcon && forcePinnedMode !== true,
    toggle,
  }
}
